import { useState } from "react";

import type { components } from "../../api/schema";
import { EMPTY_METRIC_FILTERS } from "../../lib/dashboardData";
import {
  METRIC_METADATA,
  type MetricFilters,
} from "../../lib/dashboardWidgets";
import type { MetricName } from "../../lib/dashboardMetrics";

type AlertComparator = components["schemas"]["AlertComparator"];
type AlertMessageConfig = components["schemas"]["AlertMessageConfig"];
type AlertNoDataPolicy = components["schemas"]["AlertNoDataPolicy"];
type AlertRule = components["schemas"]["AlertRuleResponse"];
type AlertSeverity = components["schemas"]["AlertSeverity"];
type AlertTriggerKind = components["schemas"]["AlertTriggerKind"];
// Derived, not restated: the generated client is the contract, so a
// target added server-side reaches this union without a hand edit.
type EventTarget = NonNullable<
  components["schemas"]["EventAlertTrigger"]["target"]
>;

function channelIdsFor(
  rule: AlertRule | undefined,
  when: "firing" | "resolved",
) {
  return (
    rule?.routes
      ?.filter((route) => route.when === when)
      .flatMap((route) => route.channel_ids) ?? []
  );
}

function initialMetricFilters(
  rule: AlertRule | undefined,
  agentId: string | undefined,
): MetricFilters {
  const trigger = rule?.trigger;
  return {
    ...EMPTY_METRIC_FILTERS,
    ...(trigger?.kind === "threshold" ? trigger.filters : undefined),
    agent_id:
      trigger?.kind === "threshold"
        ? (trigger.filters?.agent_id ?? null)
        : (agentId ?? null),
  };
}

export function useAlertRuleDraft(
  rule: AlertRule | undefined,
  agentId: string | undefined,
) {
  const initialTrigger = rule?.trigger;
  const initialMetric: MetricName =
    initialTrigger?.kind === "threshold"
      ? initialTrigger.metric
      : "regression_delta";
  const initialEvent =
    initialTrigger?.kind === "event" ? initialTrigger : undefined;
  const [displayName, setDisplayName] = useState(rule?.display_name ?? "");
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [severity, setSeverity] = useState<AlertSeverity>(
    rule?.severity ?? "warning",
  );
  const [triggerKind, setTriggerKind] = useState<AlertTriggerKind>(
    initialTrigger?.kind ?? "threshold",
  );
  const [metric, setMetric] = useState<MetricName>(initialMetric);
  const [measure, setMeasure] = useState(
    initialTrigger?.kind === "threshold"
      ? initialTrigger.measure
      : METRIC_METADATA[initialMetric].primaryMeasure,
  );
  const [metricFilters, setMetricFilters] = useState<MetricFilters>(() =>
    initialMetricFilters(rule, agentId),
  );
  const [comparator, setComparator] = useState<AlertComparator>(
    initialTrigger?.kind === "threshold" ? initialTrigger.comparator : "lt",
  );
  const [threshold, setThreshold] = useState(
    String(
      initialTrigger?.kind === "threshold" ? initialTrigger.threshold : -0.05,
    ),
  );
  const [windowSeconds, setWindowSeconds] = useState(
    String(
      initialTrigger?.kind === "threshold"
        ? initialTrigger.window_seconds
        : 3600,
    ),
  );
  const [minOccurrences, setMinOccurrences] = useState(
    String(
      initialTrigger?.kind === "threshold"
        ? initialTrigger.min_occurrences
        : 20,
    ),
  );
  const [consecutiveBreaches, setConsecutiveBreaches] = useState(
    String(
      initialTrigger?.kind === "threshold"
        ? initialTrigger.consecutive_breaches
        : 2,
    ),
  );
  const [resolveAfterClear, setResolveAfterClear] = useState(
    String(
      initialTrigger?.kind === "threshold"
        ? initialTrigger.resolve_after_clear
        : 2,
    ),
  );
  const [noDataPolicy, setNoDataPolicy] = useState<AlertNoDataPolicy>(
    initialTrigger?.kind === "threshold"
      ? initialTrigger.no_data_policy
      : "resolve",
  );
  const [renotifySeconds, setRenotifySeconds] = useState(
    String(
      initialTrigger?.kind === "threshold"
        ? initialTrigger.renotify_seconds
        : 0,
    ),
  );
  const [eventAgentId, setEventAgentId] = useState(
    initialEvent?.scope?.agent_id ?? agentId ?? "",
  );
  const [eventTarget, setEventTarget] = useState<EventTarget>(
    initialEvent?.target ?? "batch_completed",
  );
  const [templateId, setTemplateId] = useState(
    initialEvent?.scope?.template_id ?? "",
  );
  const [gateEnabled, setGateEnabled] = useState(initialEvent?.gate != null);
  const [gateComparator, setGateComparator] = useState<AlertComparator>(
    initialEvent?.gate?.comparator ?? "gte",
  );
  const [gateThreshold, setGateThreshold] = useState(
    String(initialEvent?.gate?.threshold ?? 0.9),
  );
  const [firingChannelIds, setFiringChannelIds] = useState<string[]>(
    channelIdsFor(rule, "firing"),
  );
  const [resolvedChannelIds, setResolvedChannelIds] = useState<string[]>(
    channelIdsFor(rule, "resolved"),
  );
  const [messageConfig, setMessageConfig] = useState<AlertMessageConfig>(
    rule?.message_config ?? { kind: "preset", preset: "default" },
  );

  return {
    comparator,
    consecutiveBreaches,
    displayName,
    enabled,
    eventAgentId,
    eventTarget,
    firingChannelIds,
    gateComparator,
    gateEnabled,
    gateThreshold,
    measure,
    messageConfig,
    metric,
    metricFilters,
    minOccurrences,
    noDataPolicy,
    renotifySeconds,
    resolveAfterClear,
    resolvedChannelIds,
    setComparator,
    setConsecutiveBreaches,
    setDisplayName,
    setEnabled,
    setEventAgentId,
    setEventTarget,
    setFiringChannelIds,
    setGateComparator,
    setGateEnabled,
    setGateThreshold,
    setMeasure,
    setMessageConfig,
    setMetric,
    setMetricFilters,
    setMinOccurrences,
    setNoDataPolicy,
    setRenotifySeconds,
    setResolveAfterClear,
    setResolvedChannelIds,
    setSeverity,
    setTemplateId,
    setThreshold,
    setTriggerKind,
    setWindowSeconds,
    severity,
    templateId,
    threshold,
    triggerKind,
    windowSeconds,
  };
}
